/**
 * Address-cluster suffix detection — Phase 7-IA Slice 13.
 *
 * Extracted from SectionRenderer.tsx so the review panel can share
 * one source of truth on what counts as an address sub-field. The
 * catalog stores addresses as sibling fields (no `valueType: 'address'`
 * exists today), so any consumer that wants to render an address as
 * a single unit must walk siblings by id-suffix.
 *
 * Two consumers today:
 *   - SectionRenderer: collapses address-bag fields into one
 *     AddressComposite render unit.
 *   - ParentReviewPanel: collapses address-bag fields into one
 *     dt/dd row in the review screen.
 *
 * Why suffix-detection beats bag-allowlist (Phase 7-IA Slice 12):
 * the catalog has 11+ distinct address bags and growing; the
 * sub-field suffix set is bounded by USCIS PDF conventions and
 * doesn't grow with the catalog, so suffix-driven detection is
 * stable.
 */
import type { FieldDefinition } from '@inside-us-aos/domain';

/**
 * Canonical USCIS-style address sub-field suffixes. A field is an
 * address sub-field iff its id's LAST segment is in this set.
 *
 * Added `region` — the catalog uses `region` as
 * the state/province slot in six "place" bags (birth.place,
 * marriage.place, etc.) and those fields were rendering standalone
 * outside the AddressComposite before this addition.
 */
export const ADDRESS_SUB_FIELD_NAMES: ReadonlySet<string> = new Set<string>([
  'in_care_of_name',
  'street_number_and_name',
  'unit_type',
  'unit_number',
  'city',
  'state',
  'zip_code',
  'country',
  'province',
  'postal_code',
  'region',
  // 2026-05-26 — date_from / date_to fold into the composite so the
  // "moved in / moved out" inputs render inside the address fieldset
  // rather than floating below it (raised by a customer screenshot
  // showing the dates orphaned under `most_recent_foreign_address`).
  'date_from',
  'date_to',
]);

/**
 * Returns the cluster key (= the bag prefix, everything up to and
 * including the second-to-last segment) for any field that is an
 * address sub-field, else null.
 *
 * The valueType guard remains critical (commit eecbe53) as defense
 * in depth: companion booleans like
 * `<prefix>.mailing_address.same_as_current_physical` are USCIS
 * Yes/No questions, NOT address sub-fields. Today their LAST segment
 * is not in `ADDRESS_SUB_FIELD_NAMES`, but the valueType guard
 * remains so any future boolean whose suffix collides with the
 * canonical set still falls through to FieldRenderer rather than
 * being shoved into AddressComposite's text-input path.
 */
export function getAddressClusterKey(
  field: FieldDefinition,
): string | null {
  const segments = (field.id as string).split('.');
  // Need at least <prefix>.<bag>.<sub> = 3+ segments.
  if (segments.length < 3) return null;
  const lastSegment = segments[segments.length - 1];
  if (!ADDRESS_SUB_FIELD_NAMES.has(lastSegment)) return null;
  // Only text, select, and date fields participate in the address
  // composite. Booleans / numbers with id-matching suffixes render
  // standalone via FieldRenderer. Date support added 2026-05-26 so
  // date_from / date_to fold into address bags that carry move-in /
  // move-out timestamps (address_history, most_recent_foreign_address).
  if (
    field.valueType !== 'text' &&
    field.valueType !== 'select' &&
    field.valueType !== 'date'
  ) {
    return null;
  }
  // Cluster key is everything up to and including the bag.
  return segments.slice(0, -1).join('.');
}

/**
 * Partitions a list of fields into address-bag clusters keyed by the
 * bag prefix. Fields whose id does not match an address sub-field
 * suffix go into the `null` bucket. Insertion order within each
 * bucket mirrors the input order.
 */
export function groupFieldsByAddressBag(
  fields: readonly FieldDefinition[],
): Map<string | null, FieldDefinition[]> {
  const groups = new Map<string | null, FieldDefinition[]>();
  for (const field of fields) {
    const key = getAddressClusterKey(field);
    const arr = groups.get(key) ?? [];
    arr.push(field);
    groups.set(key, arr);
  }
  return groups;
}

/**
 * Composes a single human-readable line for an address bag from
 * sibling field id → answer-string pairs. The caller (typically the
 * review panel) supplies the bag's stringified-leaf answers; this
 * helper joins them in the canonical USCIS order.
 *
 * Returns `undefined` when every part is empty so the caller can
 * suppress the row.
 */
export function formatAddressBag(
  answersBySuffix: ReadonlyMap<string, string>,
): string | undefined {
  // USCIS one-liner order: street, unit, in_care_of, city, region, country.
  // Region/postal merge so "Brooklyn, NY 11201" reads naturally.
  const parts: string[] = [];
  const street = (answersBySuffix.get('street_number_and_name') ?? '').trim();
  if (street.length > 0) parts.push(street);

  const inCareOf = (answersBySuffix.get('in_care_of_name') ?? '').trim();
  if (inCareOf.length > 0) parts.push(`c/o ${inCareOf}`);

  const unitType = (answersBySuffix.get('unit_type') ?? '').trim();
  const unitNumber = (answersBySuffix.get('unit_number') ?? '').trim();
  if (unitType.length > 0 && unitNumber.length > 0) {
    parts.push(`${unitType} ${unitNumber}`);
  } else if (unitNumber.length > 0) {
    parts.push(unitNumber);
  } else if (unitType.length > 0) {
    parts.push(unitType);
  }

  const city = (answersBySuffix.get('city') ?? '').trim();
  if (city.length > 0) parts.push(city);

  // Region: state | province | region all carry state/province
  // semantics. At most one is populated for any given bag.
  const region =
    (answersBySuffix.get('state') ?? '').trim() ||
    (answersBySuffix.get('province') ?? '').trim() ||
    (answersBySuffix.get('region') ?? '').trim();
  const postal =
    (answersBySuffix.get('zip_code') ?? '').trim() ||
    (answersBySuffix.get('postal_code') ?? '').trim();
  if (region.length > 0 && postal.length > 0) {
    parts.push(`${region} ${postal}`);
  } else if (region.length > 0) {
    parts.push(region);
  } else if (postal.length > 0) {
    parts.push(postal);
  }

  const country = (answersBySuffix.get('country') ?? '').trim();
  if (country.length > 0) parts.push(country);

  if (parts.length === 0) return undefined;
  return parts.join(', ');
}

/**
 * Returns the last segment of a field id, e.g.
 * `beneficiary.mailing_address.city` → `city`.
 */
export function addressSuffix(fieldId: string): string {
  const segments = fieldId.split('.');
  return segments[segments.length - 1];
}
